/**
 * Nutrición del planner (docs/NUTRITION.md).
 *
 * Modelo: la receta guarda valores POR PORCIÓN (`calories`, `protein_g`,
 * `carbs_g`, `fat_g`, todos opcionales). Un plato planificado aporta
 * `porPorción × porciones`, donde las porciones del plato son
 * `entry.servings ?? recipe.servings ?? 1` — la MISMA semántica de escalado
 * que la lista de compra (un plato sin porciones explícitas = la receta
 * completa a sus porciones por defecto).
 *
 * Los totales por día suman solo los platos CON datos; `missingCount` cuenta
 * los platos sin nutrición para que la UI pueda marcar el total como parcial
 * ("≈") en vez de mentir con un 0.
 */

export interface NutritionPerServing {
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
}

export interface NutritionTotals {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

export interface PlannedDishNutrition extends NutritionPerServing {
  /** Porciones planificadas del plato (entry.servings). */
  plannedServings: number | null;
  /** Porciones por defecto de la receta (recipe.servings). */
  recipeServings: number | null;
}

export interface DayNutritionSummary {
  totals: NutritionTotals;
  /** Platos que aportaron datos al total. */
  countedDishes: number;
  /** Platos SIN ningún dato de nutrición (total parcial si > 0). */
  missingCount: number;
}

/** ¿La receta tiene ALGÚN dato de nutrición? */
export function hasNutritionData(n: NutritionPerServing): boolean {
  return (
    typeof n.calories === "number" ||
    typeof n.protein_g === "number" ||
    typeof n.carbs_g === "number" ||
    typeof n.fat_g === "number"
  );
}

/** Porciones efectivas de un plato planificado (semántica de la lista de compra). */
export function effectiveServings(
  plannedServings: number | null,
  recipeServings: number | null,
): number {
  if (typeof plannedServings === "number" && Number.isFinite(plannedServings) && plannedServings > 0) {
    return plannedServings;
  }
  if (typeof recipeServings === "number" && Number.isFinite(recipeServings) && recipeServings > 0) {
    return recipeServings;
  }
  return 1;
}

/** Suma la nutrición de los platos de un día (escalada por porciones). */
export function summarizeDayNutrition(dishes: PlannedDishNutrition[]): DayNutritionSummary {
  const totals: NutritionTotals = { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };
  let countedDishes = 0;
  let missingCount = 0;
  for (const dish of dishes) {
    if (!hasNutritionData(dish)) {
      missingCount += 1;
      continue;
    }
    const servings = effectiveServings(dish.plannedServings, dish.recipeServings);
    totals.calories += (dish.calories ?? 0) * servings;
    totals.protein_g += (dish.protein_g ?? 0) * servings;
    totals.carbs_g += (dish.carbs_g ?? 0) * servings;
    totals.fat_g += (dish.fat_g ?? 0) * servings;
    countedDishes += 1;
  }
  return { totals, countedDishes, missingCount };
}

/** Redondeo estable para mostrar (1 decimal, sin -0 ni ruido flotante). */
export function roundNutrition(value: number): number {
  const rounded = Math.round(value * 10) / 10;
  return Object.is(rounded, -0) ? 0 : rounded;
}

/** Valor numérico saneado de un input de nutrición ("" → null, coma decimal). */
export function parseNutritionInput(value: string): number | null {
  const trimmed = value.trim().replace(",", ".");
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}
